import { DataTable, usePagination } from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CountrySelect,
  CountrySelectList,
} from '@/components/ui/country-select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Ellipsis } from '@/components/ui/ellipsis';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexRow } from '@/components/ui/flex';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
// Tabs imports removed - sources are no longer grouped by type
import { Textarea } from '@/components/ui/textarea';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import {
  WEBPAGE_TYPE_LABELS,
  WEBPAGE_TYPE_OPTIONS,
} from '@/pages/RegulatoryWatch/labels';
import {
  useAdminCreateSourceService,
  useAdminDeleteSourceService,
  useAdminInstitutionsService,
  useAdminSourcesService,
  useAdminUploadSourceIconService,
} from '@/services/backoffice';
import {
  getGeographyFlag,
  getGeographyLabel,
  compareGeographyCodesForFilter,
} from '@/shared/geography';
import type { RegulatorySourceItem } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import {
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { ADMIN_PATH, adminPaths, adminRegulatoryWatchPaths } from '@/paths';
import { useUrlParam, useUrlParamCsv } from '@/hooks/useUrlParam';
import { useCallback, useMemo, useRef, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';

const badgeClassName =
  'inline-flex items-center gap-[6px] px-[8px] py-[3px] rounded-[8px] text-[12px] font-medium text-[#171717] tracking-[0.18px] bg-[#FAFAFA] border border-solid border-[#E5E5E5]';

const GeographyBadge: FC<{ geography: string }> = ({ geography }) => {
  const flagImage = getGeographyFlag(geography);
  const label = getGeographyLabel(geography);

  return (
    <span className={badgeClassName}>
      {flagImage && (
        <img
          src={flagImage}
          alt=""
          className="w-[16px] h-[16px] object-contain"
        />
      )}
      <Ellipsis lineClamp={1} hasTooltip>
        {label}
      </Ellipsis>
    </span>
  );
};

// ── Source Type Table (reusable per tab) ──

const SourceTypeTable: FC<{
  sources: RegulatorySourceItem[];
  isLoading: boolean;
  onEdit: (source: RegulatorySourceItem) => void;
  onDelete: (source: RegulatorySourceItem) => void;
  isDeleting: boolean;
}> = ({ sources, isLoading, onEdit, onDelete, isDeleting }) => {
  const [searchQuery, setSearchQuery] = useUrlParam('q');
  const [selectedGeographies, setSelectedGeographies] =
    useUrlParamCsv('geography');
  const [selectedInstitutions, setSelectedInstitutions] =
    useUrlParamCsv('institution');
  const [selectedWebpageTypes, setSelectedWebpageTypes] =
    useUrlParamCsv('webpageType');

  const availableGeographies = useMemo(
    () =>
      [...new Set(sources.map(s => s.geography).filter(Boolean))].sort(
        compareGeographyCodesForFilter
      ),
    [sources]
  );

  const institutionOptions: FilterOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of sources) {
      if (s.institutionName && s.institutionId) {
        seen.set(s.institutionId, s.institutionName);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sources]);

  const webpageTypeOptions: FilterOption[] = useMemo(() => {
    const types = [
      ...new Set(sources.map(s => s.webpageType).filter(Boolean)),
    ] as string[];
    return types.map(t => ({ value: t, label: WEBPAGE_TYPE_LABELS[t] ?? t }));
  }, [sources]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const tableOptions = useMemo(
    () => ({
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      onSortingChange: setSorting,
      enableMultiSort: false,
      state: { sorting },
    }),
    [sorting]
  );

  const { pagination } = usePagination({
    initialSize: 25,
    manualPagination: false,
  });

  const filteredSources = useMemo(() => {
    let result = sources;
    if (selectedGeographies.length > 0) {
      result = result.filter(s => selectedGeographies.includes(s.geography));
    }
    if (selectedInstitutions.length > 0) {
      result = result.filter(
        s => s.institutionId && selectedInstitutions.includes(s.institutionId)
      );
    }
    if (selectedWebpageTypes.length > 0) {
      result = result.filter(
        s => s.webpageType && selectedWebpageTypes.includes(s.webpageType)
      );
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        s =>
          s.id.toLowerCase().includes(q) ||
          s.title.toLowerCase().includes(q) ||
          (s.description && s.description.toLowerCase().includes(q))
      );
    }
    return result;
  }, [
    sources,
    searchQuery,
    selectedGeographies,
    selectedInstitutions,
    selectedWebpageTypes,
  ]);

  const columns: ColumnDef<RegulatorySourceItem>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Title
          </TanstackSortableColumnHeader>
        ),
        size: 250,
        cell: ({ row }) => (
          <span className="whitespace-nowrap">{row.original.title}</span>
        ),
      },
      {
        accessorKey: 'institutionName',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Institution
          </TanstackSortableColumnHeader>
        ),
        size: 160,
        cell: ({ row }) => {
          const name = row.original.institutionName;
          if (!name) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-xs font-medium text-neutral-700">{name}</span>
          );
        },
      },
      {
        accessorKey: 'geography',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Geography
          </TanstackSortableColumnHeader>
        ),
        size: 140,
        cell: ({ row }) => (
          <GeographyBadge geography={row.original.geography} />
        ),
      },
      {
        accessorKey: 'webpageType',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Webpage type
          </TanstackSortableColumnHeader>
        ),
        size: 130,
        cell: ({ row }) => {
          const wt = row.original.webpageType;
          if (!wt) return <span className="text-muted-foreground">-</span>;
          return (
            <Badge variant="outline" className="font-normal">
              {WEBPAGE_TYPE_LABELS[wt] ?? wt}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'url',
        header: 'URL',
        size: 80,
        cell: ({ row }) => {
          const url = row.original.url;
          if (!url) return <span className="text-muted-foreground">-</span>;
          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              onClick={e => e.stopPropagation()}
            >
              Link
            </a>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const s = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => e.stopPropagation()}
                >
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(s);
                  }}
                >
                  <Pencil size={12} className="mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    if (
                      window.confirm(
                        `Delete source "${s.title}"? This cannot be undone.`
                      )
                    ) {
                      onDelete(s);
                    }
                  }}
                  disabled={isDeleting}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 size={12} className="mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [onEdit, onDelete, isDeleting]
  );

  return (
    <div className="space-y-4">
      <FlexRow className="gap-2">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
          <Input
            placeholder="Search sources..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <FilterPopoverTrigger
          label="Geography"
          isActive={selectedGeographies.length > 0}
          operator={
            selectedGeographies.length > 1
              ? 'is any of'
              : selectedGeographies.length === 1
                ? 'is'
                : undefined
          }
          activeContent={
            selectedGeographies.length === 1
              ? getGeographyLabel(selectedGeographies[0]!)
              : selectedGeographies.length > 1
                ? `${selectedGeographies.length} selected`
                : undefined
          }
          onClear={
            selectedGeographies.length > 0
              ? () => setSelectedGeographies([])
              : undefined
          }
        >
          <CountrySelectList
            countries={availableGeographies}
            selected={selectedGeographies}
            onChange={setSelectedGeographies}
          />
        </FilterPopoverTrigger>
        <FilterPopoverTrigger
          label="Institution"
          isActive={selectedInstitutions.length > 0}
          operator={
            selectedInstitutions.length > 1
              ? 'is any of'
              : selectedInstitutions.length === 1
                ? 'is'
                : undefined
          }
          activeContent={
            selectedInstitutions.length === 1
              ? institutionOptions.find(
                  o => o.value === selectedInstitutions[0]
                )?.label
              : selectedInstitutions.length > 1
                ? `${selectedInstitutions.length} selected`
                : undefined
          }
          onClear={
            selectedInstitutions.length > 0
              ? () => setSelectedInstitutions([])
              : undefined
          }
        >
          <FilterOptionList
            options={institutionOptions}
            selected={selectedInstitutions}
            onChange={setSelectedInstitutions}
          />
        </FilterPopoverTrigger>
        <FilterPopoverTrigger
          label="Webpage type"
          isActive={selectedWebpageTypes.length > 0}
          operator={
            selectedWebpageTypes.length > 1
              ? 'is any of'
              : selectedWebpageTypes.length === 1
                ? 'is'
                : undefined
          }
          activeContent={
            selectedWebpageTypes.length === 1
              ? (WEBPAGE_TYPE_LABELS[selectedWebpageTypes[0]!] ??
                selectedWebpageTypes[0])
              : selectedWebpageTypes.length > 1
                ? `${selectedWebpageTypes.length} selected`
                : undefined
          }
          onClear={
            selectedWebpageTypes.length > 0
              ? () => setSelectedWebpageTypes([])
              : undefined
          }
        >
          <FilterOptionList
            options={webpageTypeOptions}
            selected={selectedWebpageTypes}
            onChange={setSelectedWebpageTypes}
          />
        </FilterPopoverTrigger>
      </FlexRow>

      <DataTable
        columns={columns}
        data={filteredSources}
        isLoading={isLoading}
        pagination={pagination}
        rowCount={filteredSources.length}
        options={tableOptions}
        onRowClick={source => onEdit(source)}
      />
    </div>
  );
};

// ── Main SourcesTab ──

export const SourcesTab: FC = () => {
  const navigate = useNavigate();
  const { sources, isLoading } = useAdminSourcesService();
  const { deleteSource, isPending: isDeleting } = useAdminDeleteSourceService();

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const handleEdit = useCallback(
    (source: RegulatorySourceItem) => {
      navigate(
        `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.sources}/${source.id}`
      );
    },
    [navigate]
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Regulatory Sources</h2>
          <span className="text-sm text-muted-foreground">
            {sources.length} total
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() =>
              navigate(
                `${ADMIN_PATH}/${adminPaths.regulatoryWatch}/${adminRegulatoryWatchPaths.scrape}`
              )
            }
          >
            Scrape New Source
          </Button>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus size={14} className="mr-1" />
            Create Source
          </Button>
        </div>
      </div>

      <SourceTypeTable
        sources={sources}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={s => deleteSource(s.id)}
        isDeleting={isDeleting}
      />

      <CreateSourceModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
      />
    </div>
  );
};

// ── Create Source Modal ──

const CreateSourceModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = ({ open, onOpenChange }) => {
  const [id, setId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [geography, setGeography] = useState('eu');
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const [webpageType, setWebpageType] = useState('');
  const [iconFile, setIconFile] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const iconInputRef = useRef<HTMLInputElement>(null);

  const { createSource, isPending } = useAdminCreateSourceService();
  const { institutions } = useAdminInstitutionsService();
  const { uploadIcon } = useAdminUploadSourceIconService();

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setIconFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = () => {
    if (!id || !title) return;
    createSource(
      {
        id,
        title,
        description: description || undefined,
        geography,
        institutionId,
        url: url || undefined,
        webpageType: webpageType || null,
      },
      {
        onSuccess: () => {
          if (iconFile) {
            uploadIcon({ sourceId: id, file: iconFile });
          }
          onOpenChange(false);
          resetForm();
        },
      }
    );
  };

  const resetForm = () => {
    setId('');
    setTitle('');
    setDescription('');
    setGeography('eu');
    setInstitutionId(null);
    setUrl('');
    setWebpageType('');
    setIconFile(null);
    setIconPreview(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={val => {
        onOpenChange(val);
        if (!val) resetForm();
      }}
    >
      <DialogContent className="max-w-4xl min-h-[600px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Create Source</DialogTitle>
        </DialogHeader>
        <div className="flex-1 space-y-4 overflow-y-auto">
          <div className="space-y-2">
            <Label>ID (slug)</Label>
            <Input
              placeholder="e.g. fda_guidance"
              value={id}
              onChange={e => setId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              placeholder="e.g. FDA Guidances"
              value={title}
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Geography</Label>
              <CountrySelect
                value={geography || null}
                onChange={val => setGeography(val ?? '')}
                placeholder="Select geography"
              />
            </div>

            <div className="space-y-2">
              <Label>Institution (optional)</Label>
              <Select
                value={institutionId ?? '__none__'}
                onValueChange={val =>
                  setInstitutionId(val === '__none__' ? null : val)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="No institution" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {institutions.map(inst => (
                    <SelectItem key={inst.id} value={inst.id}>
                      {inst.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>URL (optional)</Label>
              <Input
                placeholder="e.g. https://example.com/news"
                value={url}
                onChange={e => setUrl(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Webpage type (optional)</Label>
              <Select value={webpageType} onValueChange={setWebpageType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select webpage type" />
                </SelectTrigger>
                <SelectContent>
                  {WEBPAGE_TYPE_OPTIONS.map(t => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Icon (optional)</Label>
            <div className="flex items-center gap-3">
              {iconPreview ? (
                <div className="relative">
                  <img
                    src={iconPreview}
                    alt="Icon preview"
                    className="w-10 h-10 object-contain rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setIconFile(null);
                      setIconPreview(null);
                      if (iconInputRef.current) iconInputRef.current.value = '';
                    }}
                    className="absolute -top-1 -right-1 rounded-full bg-destructive text-white w-4 h-4 flex items-center justify-center"
                  >
                    <X size={10} />
                  </button>
                </div>
              ) : null}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => iconInputRef.current?.click()}
              >
                <Upload size={14} className="mr-1" />
                {iconPreview ? 'Change' : 'Upload icon'}
              </Button>
              <input
                ref={iconInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/svg+xml,image/webp"
                onChange={handleIconChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex-1 space-y-2">
            <Label>Description (optional)</Label>
            <Textarea
              placeholder="Optional description..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="min-h-[200px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!id || !title || isPending}>
            {isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
